import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../organisms/Sidebar/Sidebar";
import {
  FieldType,
  ReportFieldType,
  SidebarMenuItemType,
  TabType,
} from "../../../services/helpers/types";
import { getSidebarDataForUser } from "../../../services/api/sidebar.api";
import { getTabsForSidebarItem } from "../../../services/api/tabs.api";
import { getFieldsForTab } from "../../../services/api/fields.api";
import {
  setIsFieldsLoading,
  setIsReportLoading,
  setIsSidebarLoading,
  setIsTabsLoading,
} from "../../../redux/reducers/loadingReducer";
import Messages from "../../molecules/Messages/Messages";
import Form from "../../organisms/Form/Form";
import { getFieldsForReport } from "./helpers";
import classNames from "classnames";
import SettingsPage from "../SettingsPage/SettingsPage";
import { scrollToElement } from "../../../services/helpers/commonHelpers";
import {
  setIsEditDrawerOpen,
  setIsInEditMode,
} from "../../../redux/reducers/appReducer";
import { UserType } from "../../../redux/reducers/settingsReducer";
import { useTranslation } from "react-i18next";
import AiAssistantDrawer from "../../organisms/AiAssistantDrawer/AiAssistantDrawer";

export interface CurrentSidebarItemState {
  item: SidebarMenuItemType | undefined;
  isNewItem: boolean | undefined;
}

interface MainPageProps {
  user: UserType | null;
  onLogout: (deleteUserData?: boolean) => void;
}

const MainPage = (props: MainPageProps) => {
  const { user, onLogout } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isCurrentlyInEditMode = useSelector(
    (state: any) => state.app.isInEditMode,
  );

  const [sidebarItems, setSidebarItems] = useState<SidebarMenuItemType[]>([]);
  const [currentSidebarItem, setCurrentSidebarItem] =
    useState<CurrentSidebarItemState>({
      item: undefined,
      isNewItem: false,
    });
  const [tabs, setTabs] = useState<TabType[]>([]);
  const [fields, setFields] = useState<FieldType[]>([]);
  const [fieldsForReport, setFieldsForReport] = useState<ReportFieldType[]>([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [currentTabID, setCurrentTabID] = useState<number>(0);
  const [isDictationOpen, setIsDictationOpen] = useState(false);

  const isTabletView = window.innerWidth < 1440;

  const getData = useCallback(async () => {
    if (!user || !user.userID) return;

    const disableAllLoaders = () => {
      dispatch(setIsSidebarLoading(false));
      dispatch(setIsTabsLoading(false));
      dispatch(setIsFieldsLoading(false));
      dispatch(setIsReportLoading(false));
    };

    dispatch(setIsSidebarLoading(true));
    dispatch(setIsTabsLoading(true));
    dispatch(setIsFieldsLoading(true));
    dispatch(setIsReportLoading(true));

    try {
      const sidebarData = await getSidebarDataForUser(dispatch);
      setSidebarItems(sidebarData);

      if (!sidebarData || sidebarData.length === 0) {
        disableAllLoaders();
        return;
      }

      const firstSidebarItem = sidebarData[0];
      const storedSidebarId = window.localStorage.getItem("currentSidebarItem");

      const storedSidebarIdInData = storedSidebarId
        ? sidebarData.find(
            (item) => item.sidebarItemID === parseInt(storedSidebarId, 10),
          )
        : undefined;

      const currentSidebarItem =
        storedSidebarIdInData !== undefined
          ? storedSidebarIdInData
          : firstSidebarItem;

      setCurrentSidebarItem({ item: currentSidebarItem, isNewItem: false });

      const openedId = currentSidebarItem.sidebarItemID;

      const tabsData = await getTabsForSidebarItem(dispatch, openedId);

      if (!tabsData || tabsData.length === 0) {
        setTabs([]);
        setFields([]);
        setFieldsForReport([]);
        disableAllLoaders();
        return;
      }

      setTabs(tabsData);

      const firstTab = tabsData[0];
      setCurrentTabID(firstTab.tabID);
      const fieldsData = await getFieldsForTab(t, dispatch, firstTab.tabID);
      setFields(fieldsData);

      const allFields = await getFieldsForReport(
        t,
        dispatch,
        fieldsData,
        tabsData,
      );
      setFieldsForReport(allFields);

      dispatch(setIsSidebarLoading(false));
      dispatch(setIsTabsLoading(false));
      dispatch(setIsFieldsLoading(false));
      return sidebarData;
    } catch (error) {
      console.error("Error fetching data:", error);
      disableAllLoaders();
    }
  }, [dispatch, t, user]);

  useEffect(() => {
    getData().then(() => {
      scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
    });
  }, [getData]);

  useEffect(() => {
    if (!isCurrentlyInEditMode) {
      getData().then(() => {
        scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
      });
    } else {
      scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
    }
  }, [isCurrentlyInEditMode, getData]);

  const handlePageSelect = (
    itemID?: number,
    isNewItem?: boolean,
    isRefresh?: boolean,
    newSidebarItems?: SidebarMenuItemType[],
  ) => {
    const currentSidebarItems = newSidebarItems || sidebarItems;
    if (isRefresh) {
      getData().then((sidebarData) => {
        handlePageSelect(itemID, isNewItem, false, sidebarData);
        return;
      });
    }

    if (itemID === undefined) {
      setCurrentSidebarItem({ item: undefined, isNewItem: false });
      return;
    }
    window.localStorage.setItem("currentSidebarItem", itemID.toString());
    setIsSettingsOpen(false);
    setIsAiAssistantOpen(false);
    setIsDictationOpen(false);

    const currentOpenedID = currentSidebarItem.item?.sidebarItemID;
    if (currentOpenedID !== itemID || isRefresh) {
      if (isRefresh) {
        dispatch(setIsSidebarLoading(true));
      }
      dispatch(setIsTabsLoading(true));
      dispatch(setIsFieldsLoading(true));
      dispatch(setIsReportLoading(true));
      const newSidebarItem = currentSidebarItems.find(
        (sidebarItem) => sidebarItem.sidebarItemID === itemID,
      );

      getTabsForSidebarItem(dispatch, itemID).then((tabsData) => {
        if (tabsData !== undefined && tabsData.length > 0) {
          setTabs(tabsData);
          const firstTabItemID = tabsData[0].tabID;
          setCurrentTabID(firstTabItemID);
          getFieldsForTab(t, dispatch, firstTabItemID).then((fieldsData) => {
            setFields(fieldsData);

            getFieldsForReport(t, dispatch, fieldsData, tabsData).then(
              (allFields) => {
                setFieldsForReport(allFields);
                setCurrentSidebarItem({
                  item: newSidebarItem,
                  isNewItem: isNewItem,
                });
                dispatch(setIsTabsLoading(false));
                dispatch(setIsFieldsLoading(false));
              },
            );
          });
        } else {
          setFieldsForReport([]);
          setTabs([]);
          setFields([]);
          setCurrentSidebarItem({
            item: newSidebarItem,
            isNewItem: isNewItem,
          });
          dispatch(setIsTabsLoading(false));
          dispatch(setIsFieldsLoading(false));
          dispatch(setIsReportLoading(false));
          dispatch(setIsSidebarLoading(false));
        }
      });
    }
    if (isNewItem !== undefined && isNewItem === false) {
      setCurrentSidebarItem({
        ...currentSidebarItem,
        isNewItem: isNewItem,
      });
    }
  };

  const handleSelectTab = (tabID: number) => {
    dispatch(setIsFieldsLoading(true));
    setCurrentTabID(tabID);
    getFieldsForTab(t, dispatch, tabID).then((fieldsData) => {
      setFields(fieldsData);
      dispatch(setIsFieldsLoading(false));
    });
  };

  const refreshData = () => {
    getData();
  };

  const className = classNames(
    "dui-main-page",
    {
      "dui-main-page-tablet": window.innerWidth < 1440,
    },
    { "dui-main-page-edit-mode": isCurrentlyInEditMode },
  );

  const handleSettingsOpen = () => {
    dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));
    dispatch(setIsInEditMode(false));
    setIsAiAssistantOpen(false);
    setIsSettingsOpen(!isSettingsOpen);
  };
  const handleAiAssistantOpen = () => {
    setIsSettingsOpen(false);
    setIsAiAssistantOpen(!isAiAssistantOpen);
  };
  const handlePageCreate = (itemID: number) => {
    setIsSettingsOpen(false);
    setIsAiAssistantOpen(false);

    window.localStorage.setItem("currentSidebarItem", itemID.toString());

    getData();
    setCurrentSidebarItem({
      item: sidebarItems.find(
        (sidebarItem) => sidebarItem.sidebarItemID === itemID,
      ),
      isNewItem: true,
    });
  };

  const handleCreateTab = (tabID: number, index: number) => {
    setIsSettingsOpen(false);
    setIsAiAssistantOpen(false);
    if (tabID === undefined) {
      setCurrentTabID(0);
      getData();
    }
    if (currentSidebarItem.item?.sidebarItemID !== undefined) {
      handlePageSelect(currentSidebarItem.item.sidebarItemID, undefined, true);
    }
  };

  const handleEditModeToggle = () => {
    setIsAiAssistantOpen(false);
    setIsSettingsOpen(false);
    dispatch(setIsInEditMode(!isCurrentlyInEditMode));
  };

  const handleDictationOpen = () => {
    setIsSettingsOpen(false);
    setIsAiAssistantOpen(false);
    setIsDictationOpen(!isDictationOpen);
  };

  return (
    <div className={className} id="dui-main-page">
      <Sidebar
        items={sidebarItems}
        onPageSelect={handlePageSelect}
        currentSidebarItem={currentSidebarItem}
        isTabletView={isTabletView}
        isAiAssistantOpen={isAiAssistantOpen}
        onAiAssistantOpen={handleAiAssistantOpen}
        onSettingsOpen={handleSettingsOpen}
        onPageCreate={handlePageCreate}
        onEditToggle={handleEditModeToggle}
        isDictationOpen={isDictationOpen}
        onDictationOpen={handleDictationOpen}
      ></Sidebar>
      <div className="dui-main-page-content" id="dui-main-page-content">
        <Messages logoutUser={onLogout} />
        {isSettingsOpen ? <SettingsPage onLogout={onLogout} /> : null}
        {isSettingsOpen ? null : (
          <>
            <Form
              sidebarItem={currentSidebarItem.item}
              fields={fields}
              fieldsForReport={fieldsForReport}
              tabs={tabs}
              currentTabID={currentTabID}
              onSelectTab={handleSelectTab}
              onCreateTab={handleCreateTab}
              refreshData={refreshData}
              isDictationOpen={isDictationOpen}
            />
            {isAiAssistantOpen ? (
              <AiAssistantDrawer
                isOpen={isAiAssistantOpen}
                onClose={handleAiAssistantOpen}
                widthRefElID={
                  isTabletView ? "dui-main-page" : "dui-doctors-report"
                }
                hasCollapseButton={isTabletView ? false : true}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default MainPage;
